/**
 * Permissions Matrix — Single Source of Truth
 * 
 * Defines what each role can do within each subscription tier.
 * Directly mirrors the Roles Mapping spreadsheet.
 * 
 * Access values:
 *   true              — Allowed
 *   false             — Greyed out (no upgrade prompt, role-restricted)
 *   'upgrade:pro'     — Greyed out with "Upgrade to Pro" prompt
 *   'upgrade:standard_or_pro' — Greyed out with "Upgrade to Standard or Pro" prompt
 *   'hidden'          — Not visible in the UI at all
 *   'coming_soon'     — Greyed out with "Coming Soon" badge
 */

// ── Constants ──────────────────────────────────────────────────────────

export const TIERS = {
    DEMO: 'demo',
    STANDARD: 'standard',
    PRO: 'pro',
};

export const ROLES = {
    OWNER: 'owner',
    FULL_ACCESS: 'full_access',
    EDITOR: 'editor',
    VIEWER: 'viewer',
    ADMIN: 'admin',
    SUPERADMIN: 'superadmin',
};

// Ordered from most to least privileged (used for comparison)
export const ROLE_HIERARCHY = ['superadmin', 'admin', 'owner', 'full_access', 'editor', 'viewer'];

// ── Feature Keys ───────────────────────────────────────────────────────

export const FEATURES = {
    // Main tabs
    DASHBOARD_VIEW: 'dashboard.view',
    EMISSIONS_VIEW: 'emissions.view',
    EMISSIONS_ADD_ENTRY: 'emissions.add_entry',
    EMISSIONS_ADD_CARBON_CREDIT: 'emissions.add_carbon_credit',
    EMISSIONS_OCR_IMPORT: 'emissions.ocr_import',
    EMISSIONS_BULK_CSV: 'emissions.bulk_csv',
    EMISSIONS_EDIT_ENTRY: 'emissions.edit_entry',
    EMISSIONS_DELETE_ENTRY: 'emissions.delete_entry',
    EMISSIONS_EVIDENCE_MANAGE: 'emissions.evidence_manage',
    OFFSETS_VIEW: 'offsets.view',
    OFFSETS_ADD: 'offsets.add',
    OFFSETS_DELETE: 'offsets.delete',
    OFFSETS_ALLOCATE: 'offsets.allocate',
    OFFSETS_CERTIFICATE_MANAGE: 'offsets.certificate_manage',

    // Reports
    REPORTS_SAMPLE: 'reports.sample_report',
    REPORTS_QUICK: 'reports.quick_reports',
    REPORTS_BUILDER: 'reports.report_builder',
    REPORTS_TABLE: 'reports.reports_table',

    // Grants
    GRANTS_SEARCH: 'grants.search_results',
    GRANTS_ACQUITTAL: 'grants.acquittal_export',

    // AASB S2 Lab
    AASB_S2_LAB: 'aasb_s2_lab.view',

    // Communication
    MESSAGE_CENTRE: 'message_centre.view',

    // Help
    HELP_CENTRE: 'help.help_centre',
    HELP_IDEAS_PORTAL: 'help.ideas_portal',

    // Settings
    SETTINGS_ADMIN_PANEL: 'settings.admin_panel',
    SETTINGS_MY_ACCOUNT: 'settings.my_account',
    SETTINGS_ORG_PROFILE: 'settings.org_profile',
    SETTINGS_TEAM_MGMT: 'settings.team_management',
    SETTINGS_TEAM_TRANSFER_OWNER: 'settings.team_transfer_owner',
    SETTINGS_BILLING: 'settings.billing',
    SETTINGS_ACTIVITY_LOG: 'settings.activity_log',
    SETTINGS_DATA_EXPORT: 'settings.data_export',
    SETTINGS_PUBLIC_PROFILE: 'settings.public_profile',
    SETTINGS_INTEGRATIONS: 'settings.integrations',

    // AI
    AI_COPILOT: 'ai.copilot',
};

// ── Helper to build a tier row ─────────────────────────────────────────

const allRoles = (value) => ({
    owner: value,
    full_access: value,
    editor: value,
    viewer: value,
});

// ── Permissions Matrix ─────────────────────────────────────────────────
// Structure: { [featureKey]: { [tier]: { [role]: accessValue } } }

export const PERMISSIONS = {
    // ─── Dashboard ───
    [FEATURES.DASHBOARD_VIEW]: {
        demo: allRoles(true),
        standard: allRoles(true),
        pro: allRoles(true),
    },

    // ─── Emissions ───
    [FEATURES.EMISSIONS_VIEW]: {
        demo: allRoles(true),
        standard: allRoles(true),
        pro: allRoles(true),
    },
    [FEATURES.EMISSIONS_ADD_ENTRY]: {
        demo: { owner: true, full_access: true, editor: true, viewer: 'hidden' },
        standard: { owner: true, full_access: true, editor: true, viewer: 'hidden' },
        pro: { owner: true, full_access: true, editor: true, viewer: 'hidden' },
    },
    [FEATURES.EMISSIONS_ADD_CARBON_CREDIT]: {
        demo: { owner: 'upgrade:pro', full_access: 'upgrade:pro', editor: 'upgrade:pro', viewer: 'hidden' },
        standard: { owner: 'upgrade:pro', full_access: 'upgrade:pro', editor: 'upgrade:pro', viewer: 'hidden' },
        pro: { owner: true, full_access: true, editor: true, viewer: 'hidden' },
    },
    [FEATURES.EMISSIONS_OCR_IMPORT]: {
        demo: { owner: 'upgrade:pro', full_access: 'upgrade:pro', editor: 'upgrade:pro', viewer: 'hidden' },
        standard: { owner: true, full_access: true, editor: true, viewer: 'hidden' },
        pro: { owner: true, full_access: true, editor: true, viewer: 'hidden' },
    },
    [FEATURES.EMISSIONS_BULK_CSV]: {
        demo: { owner: 'upgrade:standard_or_pro', full_access: 'upgrade:standard_or_pro', editor: 'upgrade:standard_or_pro', viewer: 'hidden' },
        standard: { owner: true, full_access: true, editor: true, viewer: 'hidden' },
        pro: { owner: true, full_access: true, editor: true, viewer: 'hidden' },
    },
    [FEATURES.EMISSIONS_DELETE_ENTRY]: {
        demo: { owner: true, full_access: true, editor: true, viewer: 'hidden' },
        standard: { owner: true, full_access: true, editor: true, viewer: 'hidden' },
        pro: { owner: true, full_access: true, editor: true, viewer: 'hidden' },
    },
    [FEATURES.EMISSIONS_EDIT_ENTRY]: {
        demo: { owner: true, full_access: true, editor: true, viewer: 'hidden' },
        standard: { owner: true, full_access: true, editor: true, viewer: 'hidden' },
        pro: { owner: true, full_access: true, editor: true, viewer: 'hidden' },
    },
    [FEATURES.EMISSIONS_EVIDENCE_MANAGE]: {
        demo: { owner: true, full_access: true, editor: true, viewer: 'hidden' },
        standard: { owner: true, full_access: true, editor: true, viewer: 'hidden' },
        pro: { owner: true, full_access: true, editor: true, viewer: 'hidden' },
    },

    // ─── Offsets ───
    [FEATURES.OFFSETS_VIEW]: {
        demo: { owner: 'upgrade:pro', full_access: 'upgrade:pro', editor: 'upgrade:pro', viewer: 'upgrade:pro' },
        standard: { owner: 'upgrade:pro', full_access: 'upgrade:pro', editor: 'upgrade:pro', viewer: 'upgrade:pro' },
        pro: allRoles(true),
    },
    [FEATURES.OFFSETS_ADD]: {
        demo: { owner: 'upgrade:pro', full_access: 'upgrade:pro', editor: 'upgrade:pro', viewer: 'hidden' },
        standard: { owner: 'upgrade:pro', full_access: 'upgrade:pro', editor: 'upgrade:pro', viewer: 'hidden' },
        pro: { owner: true, full_access: true, editor: true, viewer: 'hidden' },
    },
    [FEATURES.OFFSETS_DELETE]: {
        demo: allRoles('hidden'),
        standard: allRoles('hidden'),
        pro: { owner: true, full_access: true, editor: true, viewer: 'hidden' },
    },
    [FEATURES.OFFSETS_ALLOCATE]: {
        demo: allRoles('hidden'),
        standard: allRoles('hidden'),
        pro: { owner: true, full_access: true, editor: true, viewer: 'hidden' },
    },
    [FEATURES.OFFSETS_CERTIFICATE_MANAGE]: {
        demo: allRoles('hidden'),
        standard: allRoles('hidden'),
        pro: { owner: true, full_access: true, editor: true, viewer: 'hidden' },
    },

    // ─── Reports ───
    [FEATURES.REPORTS_SAMPLE]: {
        demo: allRoles(true),
        standard: allRoles(true),
        pro: allRoles(true),
    },
    [FEATURES.REPORTS_QUICK]: {
        demo: { owner: 'upgrade:standard_or_pro', full_access: 'upgrade:standard_or_pro', editor: false, viewer: false },
        standard: { owner: true, full_access: true, editor: true, viewer: false },
        pro: { owner: true, full_access: true, editor: true, viewer: true },
    },
    [FEATURES.REPORTS_BUILDER]: {
        demo: { owner: 'upgrade:pro', full_access: 'upgrade:pro', editor: false, viewer: false },
        standard: { owner: 'upgrade:pro', full_access: 'upgrade:pro', editor: false, viewer: false },
        pro: { owner: true, full_access: true, editor: true, viewer: false },
    },
    [FEATURES.REPORTS_TABLE]: {
        demo: allRoles(true),
        standard: allRoles(true),
        pro: allRoles(true),
    },

    // ─── Grants ───
    [FEATURES.GRANTS_SEARCH]: {
        demo: { owner: true, full_access: true, editor: false, viewer: false },
        standard: { owner: true, full_access: true, editor: false, viewer: false },
        pro: { owner: true, full_access: true, editor: true, viewer: false },
    },
    [FEATURES.GRANTS_ACQUITTAL]: {
        demo: { owner: 'upgrade:standard_or_pro', full_access: 'upgrade:standard_or_pro', editor: false, viewer: false },
        standard: { owner: true, full_access: true, editor: false, viewer: false },
        pro: { owner: true, full_access: true, editor: true, viewer: false },
    },

    // ─── AASB S2 Lab ───
    [FEATURES.AASB_S2_LAB]: {
        demo: { owner: 'upgrade:pro', full_access: 'upgrade:pro', editor: 'upgrade:pro', viewer: 'upgrade:pro' },
        standard: { owner: 'upgrade:pro', full_access: 'upgrade:pro', editor: 'upgrade:pro', viewer: 'upgrade:pro' },
        pro: { owner: true, full_access: true, editor: true, viewer: false },
    },

    // ─── Message Centre ───
    [FEATURES.MESSAGE_CENTRE]: {
        demo: allRoles(true),
        standard: allRoles(true),
        pro: allRoles(true),
    },

    // ─── Help ───
    [FEATURES.HELP_CENTRE]: {
        demo: allRoles(true),
        standard: allRoles(true),
        pro: allRoles(true),
    },
    [FEATURES.HELP_IDEAS_PORTAL]: {
        demo: { owner: true, full_access: true, editor: true, viewer: false },
        standard: allRoles(true),
        pro: allRoles(true),
    },

    // ─── Settings ───
    [FEATURES.SETTINGS_ADMIN_PANEL]: {
        demo: allRoles('hidden'),
        standard: allRoles('hidden'),
        pro: allRoles('hidden'),
        // Admin/Superadmin bypass makes this visible for them
    },
    [FEATURES.SETTINGS_MY_ACCOUNT]: {
        demo: allRoles(true),
        standard: allRoles(true),
        pro: allRoles(true),
    },
    [FEATURES.SETTINGS_ORG_PROFILE]: {
        demo: { owner: true, full_access: true, editor: false, viewer: false },
        standard: { owner: true, full_access: true, editor: false, viewer: false },
        pro: { owner: true, full_access: true, editor: false, viewer: false },
    },
    [FEATURES.SETTINGS_TEAM_MGMT]: {
        demo: { owner: true, full_access: true, editor: false, viewer: false },
        standard: { owner: true, full_access: true, editor: false, viewer: false },
        pro: { owner: true, full_access: true, editor: false, viewer: false },
    },
    [FEATURES.SETTINGS_TEAM_TRANSFER_OWNER]: {
        demo: { owner: true, full_access: false, editor: false, viewer: false },
        standard: { owner: true, full_access: false, editor: false, viewer: false },
        pro: { owner: true, full_access: false, editor: false, viewer: false },
    },
    [FEATURES.SETTINGS_BILLING]: {
        demo: { owner: true, full_access: true, editor: false, viewer: false },
        standard: { owner: true, full_access: true, editor: false, viewer: false },
        pro: { owner: true, full_access: true, editor: false, viewer: false },
    },
    [FEATURES.SETTINGS_ACTIVITY_LOG]: {
        demo: { owner: true, full_access: true, editor: false, viewer: false },
        standard: { owner: true, full_access: true, editor: false, viewer: false },
        pro: { owner: true, full_access: true, editor: false, viewer: false },
    },
    [FEATURES.SETTINGS_DATA_EXPORT]: {
        demo: allRoles(true),
        standard: allRoles(true),
        pro: allRoles(true),
    },
    [FEATURES.SETTINGS_PUBLIC_PROFILE]: {
        demo: allRoles(false),
        standard: allRoles(true),
        pro: allRoles(true),
    },
    [FEATURES.SETTINGS_INTEGRATIONS]: {
        demo: allRoles('coming_soon'),
        standard: allRoles('coming_soon'),
        pro: allRoles('coming_soon'),
    },

    // ─── AI Co-Pilot ───
    [FEATURES.AI_COPILOT]: {
        demo: { owner: 'upgrade:pro', full_access: 'upgrade:pro', editor: false, viewer: false },
        standard: { owner: 'upgrade:pro', full_access: 'upgrade:pro', editor: false, viewer: false },
        pro: allRoles(true),
    },
};

// ── Lookup Function ────────────────────────────────────────────────────

/**
 * Look up the raw access value for a feature given tier and role.
 * Admins/Superadmins inherit the same access as the 'owner' for the given tier,
 * except they always have access to the Admin Panel.
 * 
 * @param {string} feature - Feature key from FEATURES
 * @param {string} tier - Subscription tier (demo, standard, pro)
 * @param {string} role - User role (owner, full_access, editor, viewer, admin, superadmin)
 * @returns {true|false|string} The access value
 */
export function getAccess(feature, tier, role) {
    // Admin/Superadmin bypass and inheritance
    if (role === 'admin' || role === 'superadmin') {
        if (feature === FEATURES.SETTINGS_ADMIN_PANEL) return true;
        // They inherit whatever the owner can do at this tier
        role = 'owner';
    }

    const featurePerms = PERMISSIONS[feature];
    if (!featurePerms) {
        console.warn(`[Permissions] Unknown feature: ${feature}`);
        return false;
    }

    const tierPerms = featurePerms[tier || 'demo'];
    if (!tierPerms) {
        console.warn(`[Permissions] Unknown tier: ${tier} for feature: ${feature}`);
        return false;
    }

    const access = tierPerms[role || 'viewer'];
    if (access === undefined) {
        console.warn(`[Permissions] Unknown role: ${role} for feature: ${feature}`);
        return false;
    }

    return access;
}

/**
 * Check if a raw access value means "allowed".
 */
export function isAllowed(accessValue) {
    return accessValue === true;
}

/**
 * Check if a raw access value means "hidden" (not shown at all).
 */
export function isHidden(accessValue) {
    return accessValue === 'hidden';
}

/**
 * Check if a raw access value includes an upgrade prompt.
 */
export function isUpgradeRequired(accessValue) {
    return typeof accessValue === 'string' && accessValue.startsWith('upgrade:');
}

/**
 * Extract the upgrade target from an access value.
 * e.g., 'upgrade:pro' → 'pro', 'upgrade:standard_or_pro' → 'standard_or_pro'
 */
export function getUpgradeTarget(accessValue) {
    if (!isUpgradeRequired(accessValue)) return null;
    return accessValue.replace('upgrade:', '');
}
